import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageCircle, ArrowLeft, Users, Plus, Video, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { useUI } from "@/components/Layout";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Input } from "@/components/ui/input";
import NewGroupModal from "@/components/NewGroupModal";

function GroupAvatar({ size = 42 }) {
  return (
    <div style={{ width: size, height: size }} className="shrink-0 rounded-full bg-gradient-to-br from-[#6C0BA9] to-[#E024A3] flex items-center justify-center">
      <Users className="text-white" style={{ width: size * 0.5, height: size * 0.5 }} />
    </div>
  );
}

export default function ChatDrawer({ chatUser, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { send, subscribe } = useWS();
  const { startCall, openReport } = useUI();
  const navigate = useNavigate();
  const [active, setActive] = useState(null); // {kind:'direct'|'group', ...}
  const [text, setText] = useState("");
  const [typingFrom, setTypingFrom] = useState(null);
  const [otherRead, setOtherRead] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const activeRef = useRef(null);

  const open = !!chatUser;
  activeRef.current = active;

  useEffect(() => {
    if (chatUser && chatUser.user_id) setActive({ kind: "direct", ...chatUser });
    else if (chatUser) setActive(null);
  }, [chatUser]);

  const isDirect = active?.kind === "direct";
  const isGroup = active?.kind === "group";
  const activeId = isDirect ? active?.user_id : active?.id;

  const { data: convos = [] } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => (await api.get("/messages/conversations")).data,
    enabled: open,
  });
  const { data: groups = [] } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/groups")).data,
    enabled: open,
  });

  const { data: directMsgs = [] } = useQuery({
    queryKey: ["messages", activeId],
    queryFn: async () => (await api.get(`/messages/${activeId}`)).data,
    enabled: isDirect && !!activeId,
  });
  const { data: groupMsgs = [] } = useQuery({
    queryKey: ["group-messages", activeId],
    queryFn: async () => (await api.get(`/groups/${activeId}/messages`)).data,
    enabled: isGroup && !!activeId,
  });
  const messages = isGroup ? groupMsgs : directMsgs;

  useEffect(() => {
    return subscribe((data) => {
      const act = activeRef.current;
      if (data.event === "message") {
        const other = data.message?.from_id;
        if (act?.kind === "direct" && other === act.user_id) {
          qc.invalidateQueries({ queryKey: ["messages", act.user_id] });
          send({ event: "read", other_id: act.user_id });
        }
        qc.invalidateQueries({ queryKey: ["conversations"] });
      } else if (data.event === "group_message") {
        if (act?.kind === "group" && data.group_id === act.id) {
          qc.invalidateQueries({ queryKey: ["group-messages", act.id] });
        }
        qc.invalidateQueries({ queryKey: ["groups"] });
      } else if (data.event === "group_created") {
        qc.invalidateQueries({ queryKey: ["groups"] });
      } else if (data.event === "typing") {
        if (act?.kind === "direct" && data.from_id === act.user_id) {
          setTypingFrom(data.from_id);
          clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingFrom(null), 2500);
        }
      } else if (data.event === "read") {
        if (act?.kind === "direct" && data.from_id === act.user_id) setOtherRead(true);
      }
    });
  }, [subscribe, send, qc]);

  useEffect(() => {
    if (isDirect && activeId) {
      setOtherRead(false);
      send({ event: "read", other_id: activeId });
    }
  }, [isDirect, activeId, send]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingFrom]);

  const onType = (e) => {
    setText(e.target.value);
    if (isDirect && activeId && Date.now() - lastTypingSent.current > 1200) {
      lastTypingSent.current = Date.now();
      send({ event: "typing", to_id: activeId });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const content = text;
    setText("");
    setOtherRead(false);
    try {
      if (isGroup) {
        await api.post(`/groups/${activeId}/messages`, { content });
        qc.invalidateQueries({ queryKey: ["group-messages", activeId] });
        qc.invalidateQueries({ queryKey: ["groups"] });
      } else {
        await api.post(`/messages/${activeId}`, { content });
        qc.invalidateQueries({ queryKey: ["messages", activeId] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
      }
    } catch (err) {
      setText(content);
      const detail = formatApiError(err.response?.data?.detail);
      const locked = err.response?.status === 403 && detail.startsWith("Verify");
      toast.error(detail, locked ? { action: { label: "Verify now", onClick: () => { onClose(); navigate("/settings"); } } } : undefined);
    }
  };

  const myMessages = messages.filter((m) => m.from_id === user.user_id);
  const lastMine = myMessages[myMessages.length - 1];
  const lastMineSeen = isDirect && lastMine && (otherRead || lastMine.read);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 400, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 400, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          data-testid="chat-drawer"
          className="fixed bottom-0 right-4 w-[92vw] sm:w-96 h-[500px] max-h-[75vh] bg-card rounded-t-2xl border-t border-x border-border shadow-2xl z-[60] flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-gradient-to-r from-[#6C0BA9] to-[#8012C6]">
            {active && (
              <button data-testid="chat-back-btn" onClick={() => setActive(null)} className="text-white/90 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
            )}
            {!active && <MessageCircle className="w-5 h-5 text-white" />}
            <span className="font-heading font-bold text-white flex items-center gap-1 truncate">
              {isDirect ? <>{active.name}{active.verified && <VerifiedBadge size={13} />}</> : isGroup ? active.name : "Messages"}
            </span>
            {isGroup && <span className="text-xs text-white/70">· {active.member_count}</span>}
            {active && (
              <button
                data-testid="start-call-btn"
                onClick={() => startCall(isGroup ? "group" : "direct", activeId)}
                className="ml-auto w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                title="Start video call"
              >
                <Video className="w-4 h-4" />
              </button>
            )}
            {!active && (
              <button data-testid="new-group-btn" onClick={() => setNewGroupOpen(true)} className="ml-auto mr-1 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" title="New group">
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button data-testid="chat-close-btn" onClick={onClose} className={`text-white/90 hover:text-white ${active || true ? "" : "ml-auto"}`}><X className="w-5 h-5" /></button>
          </div>

          {!active ? (
            <div className="flex-1 overflow-y-auto">
              {groups.map((g) => (
                <button
                  key={g.id}
                  data-testid={`group-convo-${g.id}`}
                  onClick={() => setActive({ kind: "group", ...g })}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                >
                  <GroupAvatar size={42} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{g.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{g.last_message || `${g.member_count} members`}</div>
                  </div>
                </button>
              ))}
              {convos.length === 0 && groups.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No conversations yet. Start a chat from a contact or create a group.</div>
              ) : (
                convos.map((c) => (
                  <button
                    key={c.user.user_id}
                    data-testid={`convo-${c.user.user_id}`}
                    onClick={() => setActive({ kind: "direct", ...c.user })}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                  >
                    <Avatar user={c.user} size={42} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold flex items-center gap-1 truncate">{c.user.name}{c.user.verified && <VerifiedBadge size={12} />}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.last_message}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                {messages.map((m) => {
                  const mine = m.from_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex group ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] ${mine ? "" : "flex gap-2"}`}>
                        {!mine && isGroup && <Avatar user={m.sender} size={26} />}
                        <div>
                          {!mine && isGroup && <div className="text-[10px] text-muted-foreground mb-0.5 ml-1">{m.sender?.name}</div>}
                          <div className={`px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-[#6C0BA9] text-white rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                            {m.content}
                          </div>
                        </div>
                        {!mine && (
                          <button
                            data-testid={`report-message-${m.id}`}
                            onClick={() => openReport({ type: isGroup ? "group_message" : "message", id: m.id, label: "message" })}
                            title="Report message"
                            className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-[#E024A3] transition-opacity"
                          >
                            <Flag className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {lastMineSeen && (
                  <div className="flex justify-end pr-1"><span data-testid="seen-indicator" className="text-[10px] text-muted-foreground">Seen</span></div>
                )}
                <AnimatePresence>
                  {typingFrom && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="typing-indicator" className="flex justify-start">
                      <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground" animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.9, delay: i * 0.15 }} />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div ref={bottomRef} />
              </div>
              <form onSubmit={submit} className="p-3 border-t border-border flex gap-2">
                <Input data-testid="chat-input" value={text} onChange={onType} placeholder="Type a message..." className="rounded-full bg-input border-border h-10" />
                <button data-testid="chat-send-btn" type="submit" className="w-10 h-10 shrink-0 rounded-full bg-[#6C0BA9] hover:bg-[#8012C6] text-white flex items-center justify-center transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </motion.div>
      )}
      <NewGroupModal open={newGroupOpen} onClose={() => setNewGroupOpen(false)} onCreated={(g) => setActive({ kind: "group", ...g })} />
    </AnimatePresence>
  );
}
